import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, IconButton } from "@mui/material";
import { CalendarClock, Menu, Save } from "lucide-react";
import axios from "axios";
import DoctorHeader from "./DoctorHeader";
import DoctorSidebar, { DoctorSidebarDrawer } from "./DoctorSidebar";
import DoctorFooter from "./DoctorFooter";
import SimpleSnackbar from "../Snackbar";
import AvailabilityCalendarEditor, {
  buildOneWeekCalendar,
  getAvailabilityWeekProgress,
  isAvailabilityWeekComplete,
  sanitizeCalendarForSave,
} from "./AvailabilityCalendarEditor";

const DoctorAvailabilityPage = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [calendar, setCalendar] = useState(() => buildOneWeekCalendar());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState("success");
  const [lastUpdated, setLastUpdated] = useState("");

  const doctor = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("doctor") || "{}");
    } catch {
      return {};
    }
  }, []);

  const token = localStorage.getItem("token") || "";

  const fetchAvailability = useCallback(async () => {
    try {
      setLoading(true);

      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/doctor/availability`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const updatedAt = res.data?.availabilityCalendarUpdatedAt || "";
      setCalendar(
        buildOneWeekCalendar(res.data?.availabilityCalendar || [], {
          markExisting: Boolean(updatedAt),
        })
      );
      setLastUpdated(res.data?.availabilityCalendarUpdatedAt || "");
    } catch (error) {
      console.error(error);
      setMessage("Could not load availability");
      setStatus("error");
    } finally {
      setLoading(false);
    }
  }, [token]);

  const saveAvailability = async () => {
    if (!isAvailabilityWeekComplete(calendar)) {
      setMessage("Please set all 7 days before saving");
      setStatus("error");
      return;
    }

    try {
      setSaving(true);

      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/doctor/availability`,
        {
          availabilityCalendar: sanitizeCalendarForSave(calendar),
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (res.data?.doctor) {
        const nextDoctor = {
          ...doctor,
          availabilityCalendar: res.data.doctor.availabilityCalendar,
          availabilityCalendarUpdatedAt:
            res.data.doctor.availabilityCalendarUpdatedAt,
        };
        localStorage.setItem("doctor", JSON.stringify(nextDoctor));
      }

      setLastUpdated(res.data?.doctor?.availabilityCalendarUpdatedAt || "");
      setMessage("Availability saved");
      setStatus("success");
    } catch (error) {
      console.error(error);
      setMessage(error.response?.data?.message || "Could not save availability");
      setStatus("error");
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    fetchAvailability();
  }, [fetchAvailability]);

  const availableDays = calendar.filter(
    (day) =>
      day.enabled && (day.slots || []).some((slot) => slot.start && slot.end)
  ).length;
  const weekProgress = getAvailabilityWeekProgress(calendar);

  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      <DoctorHeader />
      <SimpleSnackbar
        message={message}
        color={status}
        onClose={() => setMessage("")}
      />

      <div className="flex items-center justify-between border-b border-slate-200 bg-white px-4 py-3 md:hidden">
        <div>
          <p className="text-xs text-slate-400">Dashboard</p>
          <h1 className="text-base font-semibold text-slate-950">
            Availability
          </h1>
        </div>

        <IconButton onClick={() => setDrawerOpen(true)}>
          <Menu />
        </IconButton>
      </div>

      <div className="flex flex-1 overflow-hidden">
        <div className="hidden lg:flex">
          <DoctorSidebar activeTab="Availability" setActiveTab={() => {}} />
        </div>

        <DoctorSidebarDrawer
          open={drawerOpen}
          onOpen={() => setDrawerOpen(true)}
          onClose={() => setDrawerOpen(false)}
          activeTab="Availability"
          setActiveTab={() => setDrawerOpen(false)}
        />

        <main className="flex-1 overflow-y-auto">
          <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6 lg:px-8">
            <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
              <div>
              
                <h1 className="text-2xl hidden lg:block font-semibold tracking-[-0.03em] text-slate-950 sm:text-3xl">
                  Availability
                </h1>
                <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-500">
                  Choose the days and times you can attend to patients for the
                  next one week.
                </p>
              </div>

              <Button
                onClick={saveAvailability}
                disabled={saving || loading || !weekProgress.isComplete}
                startIcon={<Save size={16} />}
                sx={{
                  minHeight: 44,
                  bgcolor: "#075E54",
                  color: "white",
                  borderRadius: "12px",
                  textTransform: "none",
                  fontWeight: 600,
                  px: 3,
                  boxShadow: "none",
                  "&:hover": { bgcolor: "#064b43", boxShadow: "none" },
                  "&:disabled": {
                    bgcolor: "#e2e8f0",
                    color: "#64748b",
                  },
                }}
              >
                {saving
                  ? "Saving..."
                  : weekProgress.isComplete
                  ? "Save calendar"
                  : "Set all days first"}
              </Button>
            </div>

            <section className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div className="rounded-2xl border border-slate-200 bg-white p-4">
                <p className="text-sm text-slate-500">Available days</p>
                <p className="mt-2 text-2xl font-semibold text-slate-950">
                  {availableDays}
                </p>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white p-4">
                <p className="text-sm text-slate-500">Days set</p>
                <p className="mt-2 text-2xl font-semibold text-slate-950">
                  {weekProgress.completeDays}/{weekProgress.totalDays}
                </p>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white p-4">
                <p className="text-sm text-slate-500">Last update</p>
                <p className="mt-2 text-sm font-semibold text-slate-950">
                  {lastUpdated
                    ? new Date(lastUpdated).toLocaleString("en-NG")
                    : "Not updated yet"}
                </p>
              </div>
            </section>

            {loading ? (
              <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center text-sm text-slate-500">
                Loading availability...
              </div>
            ) : (
              <AvailabilityCalendarEditor
                value={calendar}
                onChange={setCalendar}
              />
            )}
          </div>
        </main>
      </div>

    </div>
  );
};

export default DoctorAvailabilityPage;
